/**
 * PrintersList — Impresoras de red (estándar de listados del CMDB).
 *
 * El andamiaje lo pone `common/ListPage.jsx`: buscador, filtros, exportar a
 * Excel, columnas, cebra, clic en la fila para abrir la ficha EN LECTURA y los
 * botones `[acciones propias] · Copiar · Editar · Eliminar`.
 *
 * Lo propio de una impresora:
 *  · Vistas: **Lista** (por defecto) y **Fichas**.
 *  · Filtros: Estado, Ubicación, Fabricante y **conectividad** (Ethernet, WiFi
 *    o sin red).
 *  · Acciones propias: **Ver conexión Ethernet** (mapa de puertos) y **WiFi**
 *    (sus redes configuradas). Cada una solo aparece si la impresora la tiene.
 *
 * Ojo con el mapa de puertos: hay que pasarle `companyId` y `onUpdated`, que es
 * como se llama el prop del componente (antes se le pasaban `isOpen` y
 * `onPortUpdate`, que ignoraba: no refrescaba nada).
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  PrinterIcon,
  EyeIcon,
  WifiIcon,
  ListBulletIcon,
  Squares2X2Icon,
} from '@heroicons/react/24/outline';
import api from '../services/api';
import ListPage from './common/ListPage';
import BulkPlacementAction from './common/BulkPlacementAction';
import BulkEditAction from './common/BulkEditAction';
import PortMapModal from './PortMapModal';
import PrinterForm from './PrinterForm';
import { useListView } from '../views/ViewsContext';
import { useAutoOpen } from '../hooks/useAutoOpen';
import useNavOrder from '../hooks/useNavOrder';
import { buildLocationMap, locationPath, locationPathOptions } from '../utils/locationTree';
import { getServerFormStyle } from '../utils/serverFormStyle';

const STATUS_META = {
  active:      { label: 'Activo',        badge: 'list-badge--on' },
  inactive:    { label: 'Inactivo',      badge: 'list-badge--off' },
  maintenance: { label: 'Mantenimiento', badge: 'list-badge--warn' },
};

function PrintersList({ companyId }) {
  const { t } = useTranslation();
  const tr = useCallback((key, fallback) => {
    const value = t(key);
    return !value || value === key ? fallback : value;
  }, [t]);
  const [searchParams, setSearchParams] = useSearchParams();

  const [printers, setPrinters] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [bulkResetKey, setBulkResetKey] = useState(0);

  const [search, setSearch] = useState('');
  const [filterStatus, setFilterStatus] = useState('');
  const [filterLoc, setFilterLoc] = useState('');
  const [filterMaker, setFilterMaker] = useState('');
  const [filterNet, setFilterNet] = useState('');
  const [hlId, setHlId] = useState(null);

  const [locations, setLocations] = useState([]);
  const estiloFicha = getServerFormStyle();
  const mapaUbicaciones = useMemo(() => buildLocationMap(locations), [locations]);
  /** Ruta completa («Sede › Planta 2 › Sala 3»); si no hay árbol, el nombre suelto. */
  const rutaUbicacion = useCallback(
    (item) => (item?.location_id ? locationPath(mapaUbicaciones, item.location_id) : '') || item?.location_name || '',
    [mapaUbicaciones],
  );
  const [showForm, setShowForm] = useState(false);
  const [formMode, setFormMode] = useState('view');
  const [editItem, setEditItem] = useState(null);
  const [cloneSource, setCloneSource] = useState(null);
  const [showPortModal, setShowPortModal] = useState(false);
  const [selectedPrinterForPort, setSelectedPrinterForPort] = useState(null);
  const [showWiFiModal, setShowWiFiModal] = useState(false);
  const [wifiConfigs, setWiFiConfigs] = useState([]);

  // Resalta el indicado con ?destacar=<id>[&abrir=1]
  useEffect(() => {
    const raw = searchParams.get('destacar');
    if (!raw || !printers.length) return undefined;
    const id = Number(raw);
    const target = printers.find((p) => p.id === id);
    if (!target) return undefined;
    const abrir = searchParams.get('abrir') === '1';
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      next.delete('destacar');
      next.delete('abrir');
      return next;
    }, { replace: true });
    if (abrir) { handleOpen(target); return undefined; }
    setHlId(id);
    const row = document.getElementById(`printer-row-${id}`);
    if (row) row.scrollIntoView({ behavior: 'smooth', block: 'center' });
    const timer = setTimeout(() => setHlId(null), 4000);
    return () => clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams, printers, setSearchParams]);

  // Load data — re-run whenever the active company changes
  useEffect(() => {
    if (!companyId) {
      setPrinters([]);
      setLocations([]);
      setLoading(false);
      setError('Selecciona una empresa para ver impresoras.');
      return;
    }
    fetchPrinters();
    fetchLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const fetchPrinters = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await api.getPrinters({ company_id: companyId });
      if (response.data && Array.isArray(response.data)) {
        setPrinters(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setPrinters(response.data.data);
      } else {
        setPrinters([]);
      }
    } catch (err) {
      console.error('Failed to fetch printers:', err);
      setError('Error cargando impresoras');
    } finally {
      setLoading(false);
    }
  };

  const fetchLocations = async () => {
    try {
      const response = await api.getLocations('', companyId);
      if (response.data && Array.isArray(response.data)) {
        setLocations(response.data);
      } else if (response.data && Array.isArray(response.data.data)) {
        setLocations(response.data.data);
      }
    } catch (err) {
      console.error('Failed to fetch locations:', err);
    }
  };

  const filteredPrinters = useMemo(() => {
    const q = search.trim().toLowerCase();
    return printers.filter((p) => {
      const matchesSearch = !q
        || (p.code || '').toLowerCase().includes(q)
        || (p.name || '').toLowerCase().includes(q)
        || (p.model || '').toLowerCase().includes(q)
        || (p.manufacturer || '').toLowerCase().includes(q)
        || (p.ip_address || '').toLowerCase().includes(q)
        || (p.mac_address || '').toLowerCase().includes(q);
      const matchesStatus = !filterStatus || (p.status || 'active') === filterStatus;
      const matchesLoc = !filterLoc || String(p.location_id) === filterLoc;
      const matchesMaker = !filterMaker || (p.manufacturer || '') === filterMaker;
      const matchesNet = !filterNet
        || (filterNet === 'ethernet' ? !!p.has_ethernet
          : filterNet === 'wifi' ? !!p.has_wifi
            : !p.has_ethernet && !p.has_wifi);
      return matchesSearch && matchesStatus && matchesLoc && matchesMaker && matchesNet;
    });
  }, [printers, search, filterStatus, filterLoc, filterMaker, filterNet]);

  // Orden de las fichas congelado mientras la ficha está abierta.
  const navPrinters = useNavOrder(filteredPrinters, editItem, showForm);

  const makerOptions = useMemo(
    () => [...new Set(printers.map((p) => (p.manufacturer || '').trim()).filter(Boolean))].sort(),
    [printers],
  );

  /* ── Ficha (lectura por defecto) ─────────────────────────────────────── */
  const handleOpen = (printer) => { setCloneSource(null); setEditItem(printer); setFormMode('view'); setShowForm(true); };
  const handleEdit = (printer) => { setCloneSource(null); setEditItem(printer); setFormMode('edit'); setShowForm(true); };
  const handleCreate = () => { setCloneSource(null); setEditItem(null); setFormMode('edit'); setShowForm(true); };
  useAutoOpen(printers, handleOpen);

  const closeForm = () => {
    setShowForm(false);
    setEditItem(null);
    setCloneSource(null);
    fetchPrinters();
  };

  const handleDelete = async (printer) => {
    try {
      await api.deletePrinter(printer.id, companyId);
      await fetchPrinters();
    } catch (err) {
      setError(err.message || tr('printer.delete_error', 'No se pudo eliminar la impresora'));
    }
  };

  const handleShowWiFi = async (printer) => {
    if (!printer.has_wifi) return;
    try {
      const response = await api.getPrinterWiFiConfigs(printer.id);
      setWiFiConfigs(response.data || []);
      setSelectedPrinterForPort(printer);
      setShowWiFiModal(true);
    } catch (error) {
      setError(error.message || tr('printer.wifi_error', 'No se pudieron cargar las redes WiFi'));
    }
  };

  const handleShowPort = (printer) => {
    setSelectedPrinterForPort(printer);
    setShowPortModal(true);
  };

  const handleClone = (printer) => {
    if (!printer) return;
    setCloneSource({
      ...printer,
      id: undefined,
      code: printer.code ? `${printer.code}-copia` : '',
      name: printer.name ? `${printer.name} (copia)` : '',
      ip_address: '',
      mac_address: '',
      serial_number: '',
      status: 'active',
    });
    setEditItem(null);
    setFormMode('edit');
    setShowForm(true);
  };

  /* ── Columnas (9; 7 visibles de entrada) ─────────────────────────────── */
  const columns = useMemo(() => {
    const dash = <span className="list-hint">—</span>;
    return [
      {
        key: 'code',
        label: tr('printer.code', 'Código'),
        sortable: true,
        render: (printer) => (
          <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
            <PrinterIcon style={{ width: 15, height: 15, color: 'var(--text-muted)' }} />
            {printer.code ? <span className="list-code">{printer.code}</span> : dash}
          </span>
        ),
      },
      { key: 'name', label: tr('list.name', 'Nombre'), sortable: true, render: (printer) => printer.name || dash },
      { key: 'model', label: tr('printer.model', 'Modelo'), sortable: true, render: (printer) => printer.model || dash },
      { key: 'manufacturer', label: tr('printer.manufacturer', 'Fabricante'), sortable: true, render: (printer) => printer.manufacturer || dash },
      { key: 'ip_address', label: 'IP', sortable: true, render: (printer) => (printer.ip_address ? <span className="list-code">{printer.ip_address}</span> : dash) },
      { key: 'mac_address', label: 'MAC', sortable: true, render: (printer) => (printer.mac_address ? <span className="list-code">{printer.mac_address}</span> : dash) },
      {
        key: 'connectivity',
        label: tr('printer.connectivity', 'Conectividad'),
        sortable: true,
        sortValue: (printer) => `${printer.has_ethernet ? 1 : 0}${printer.has_wifi ? 1 : 0}`,
        render: (printer) => (
          <span style={{ display: 'inline-flex', gap: 4 }}>
            {printer.has_ethernet && <span className="list-badge list-badge--on">Ethernet</span>}
            {printer.has_wifi && <span className="list-badge list-badge--on">WiFi</span>}
            {!printer.has_ethernet && !printer.has_wifi && <span className="list-hint">Sin red</span>}
          </span>
        ),
      },
      {
        key: 'location_name',
        label: tr('list.location', 'Ubicación'),
        sortable: true,
        sortValue: (printer) => rutaUbicacion(printer),
        render: (printer) => rutaUbicacion(printer) || dash,
      },
      {
        key: 'status',
        label: tr('list.status', 'Estado'),
        sortable: true,
        sortValue: (printer) => printer.status || 'active',
        render: (printer) => {
          const meta = STATUS_META[printer.status] || STATUS_META.active;
          return <span className={`list-badge ${meta.badge}`}>{meta.label}</span>;
        },
      },
    ];
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [t, tr, rutaUbicacion]);

  const defaultVisible = ['code', 'name', 'model', 'manufacturer', 'ip_address', 'connectivity', 'location_name', 'status'];

  const filters = [
    {
      key: 'status',
      label: tr('printer.filter_status', 'Estado'),
      allLabel: tr('printer.filter_status_all', 'Todos los estados'),
      value: filterStatus,
      onChange: setFilterStatus,
      options: Object.entries(STATUS_META).map(([value, meta]) => ({ value, label: meta.label })),
    },
    {
      key: 'location',
      label: tr('list.filter_location', 'Ubicación'),
      allLabel: tr('list.filter_all_locations', 'Todas las ubicaciones'),
      value: filterLoc,
      onChange: setFilterLoc,
      options: locationPathOptions(locations),
    },
    {
      key: 'maker',
      label: tr('printer.filter_maker', 'Fabricante'),
      allLabel: tr('printer.filter_maker_all', 'Todos los fabricantes'),
      value: filterMaker,
      onChange: setFilterMaker,
      options: makerOptions.map((v) => ({ value: v, label: v })),
    },
    {
      key: 'net',
      label: tr('printer.filter_net', 'Conectividad'),
      allLabel: tr('printer.filter_net_all', 'Cualquier conectividad'),
      value: filterNet,
      onChange: setFilterNet,
      options: [
        { value: 'ethernet', label: tr('printer.net_ethernet', 'Con Ethernet') },
        { value: 'wifi', label: tr('printer.net_wifi', 'Con WiFi') },
        { value: 'none', label: tr('printer.net_none', 'Sin red') },
      ],
    },
  ];

  const subtitle = `${printers.length} ${tr('printer.plural', 'impresoras')}`
    + ` · ${filteredPrinters.length} ${tr('location.filtered', 'filtradas')}`;

  const exportRows = useMemo(() => filteredPrinters.map(printer => {
    return {
      'Código': printer.code || '',
      'Nombre': printer.name || '',
      'Modelo': printer.model || '',
      'Fabricante': printer.manufacturer || '',
      'IP': printer.ip_address || '',
      'MAC': printer.mac_address || '',
      'Ethernet': printer.has_ethernet ? 'Sí' : 'No',
      'WiFi': printer.has_wifi ? 'Sí' : 'No',
      'Ubicación': rutaUbicacion(printer),
      'Estado': (STATUS_META[printer.status] || STATUS_META.active).label,
    };
  }), [filteredPrinters, rutaUbicacion]);

  /** Tarjetas: lo esencial de la impresora, con los mismos botones de fila. */
  function renderCards({ rows, rowActions }) {
    return (
      <div className="list-cards">
        {rows.map((printer) => {
          const meta = STATUS_META[printer.status] || STATUS_META.active;
          const locationName = rutaUbicacion(printer);
          return (
            <div
              key={printer.id}
              id={`printer-card-${printer.id}`}
              className={`list-card${printer.id === hlId ? ' list-row-highlight' : ''}`}
              role="button"
              tabIndex={0}
              onClick={() => handleOpen(printer)}
              onKeyDown={(event) => { if (event.key === 'Enter') handleOpen(printer); }}
              title={`${tr('action.view', 'Ver')} ${printer.name}`}
            >
              <div className="list-card-head">
                <div>
                  <div className="list-card-title">🖨️ {printer.name}</div>
                  {printer.code && <span className="list-code">{printer.code}</span>}
                </div>
                <span className={`list-badge ${meta.badge}`}>{meta.label}</span>
              </div>

              <div className="list-card-meta">
                {printer.model && <span>{printer.model}</span>}
                {printer.manufacturer && <span>🔧 {printer.manufacturer}</span>}
                {printer.ip_address && <span className="list-code">{printer.ip_address}</span>}
                {printer.mac_address && <span className="list-code">{printer.mac_address}</span>}
                {locationName && <span>📍 {locationName}</span>}
              </div>

              <div className="list-card-meta">
                {printer.has_ethernet && <span className="list-badge list-badge--on">Ethernet</span>}
                {printer.has_wifi && <span className="list-badge list-badge--on">WiFi</span>}
              </div>

              <div className="list-card-actions">{rowActions(printer)}</div>
            </div>
          );
        })}
      </div>
    );
  }

  /* ── Vistas ───────────────────────────────────────────────────────────── */
  const PRINTER_VIEWS = useMemo(() => [
    { key: 'list', label: tr('printer.view_list', 'Lista'), icon: ListBulletIcon, title: tr('printer.view_list_hint', 'Tabla con todas las columnas') },
    { key: 'card', label: tr('printer.view_cards', 'Fichas'), icon: Squares2X2Icon, title: tr('printer.view_cards_hint', 'Tarjetas con lo esencial de cada impresora'), render: renderCards },
    // eslint-disable-next-line react-hooks/exhaustive-deps
  ], [t, hlId, locations]);
  useListView('printers', 'list');

  if (!companyId) {
    return (
      <div className="list-page" style={{ padding: '1.5rem' }}>
        <div className="alert alert-warning">{tr('printer.select_company', 'Selecciona una empresa para ver impresoras.')}</div>
      </div>
    );
  }

  return (
    <>
      {showForm && (
        <PrinterForm
          printer={cloneSource || editItem}
          companyId={companyId}
          initialMode={formMode}
          variant={estiloFicha === 'cards' ? 'cards' : 'classic'}
          navItems={navPrinters}
          navIndex={editItem ? navPrinters.findIndex(p => String(p.id) === String(editItem.id)) : -1}
          onNavigate={(destino) => setEditItem(destino)}
          onClose={closeForm}
          onSave={(creado) => {
            fetchPrinters();
            // Alta: el listado adopta el registro creado y la ficha sigue abierta.
            if (creado?.id) { setEditItem(creado); setFormMode('edit'); }
          }}
        />
      )}

      <div style={{ display: showForm ? 'none' : undefined }}>
        <ListPage
          icon={PrinterIcon}
          title={tr('printer.title', 'Impresoras de red')}
          subtitle={subtitle}
          onCreate={handleCreate}
          createLabel={tr('printer.new', 'Nueva impresora')}
          createDisabled={!companyId}

          search={search}
          onSearchChange={setSearch}
          searchPlaceholder={tr('printer.search_placeholder', 'Buscar por código, nombre, modelo, fabricante, IP…')}
          filters={filters}
          exportRows={exportRows}
          exportFileBase="impresoras"
          exportSheetName="Impresoras"

          columns={columns}
          columnStorageKey="cmdb_cols_printers"
          companyId={companyId}
          importEntity="printers"
          onImported={() => fetchPrinters()}
          defaultVisible={defaultVisible}
          rows={filteredPrinters}
          rowKey={(printer) => printer.id}
          rowId={(printer) => `printer-row-${printer.id}`}
          rowClassName={(printer) => (printer.id === hlId ? 'list-row-highlight' : '')}
          onRowClick={handleOpen}
          rowTitle={(printer) => `${tr('action.view', 'Ver')} ${printer.name}`}

          selectable
          selectionResetKey={bulkResetKey}
          bulkActions={({ selectedRows }) => (
            <>
              <BulkPlacementAction
                companyId={companyId}
                entity="printers"
                supportsRack={false}
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchPrinters();
                }}
              />
              <BulkEditAction
                companyId={companyId}
                entity="printers"
                selectedRows={selectedRows}
                onApplied={() => {
                  setBulkResetKey((key) => key + 1);
                  fetchPrinters();
                }}
              />
            </>
          )}

          views={PRINTER_VIEWS}
          listKey="printers"
          defaultView="list"

          loading={loading}
          loadingText={tr('msg.loading', 'Cargando impresoras…')}
          error={error}

          empty={{
            icon: '🖨️',
            title: printers.length === 0
              ? tr('printer.empty', 'No hay impresoras registradas')
              : tr('msg.no_results', 'Ninguna impresora coincide con los filtros'),
            subtitle: printers.length === 0 ? tr('printer.empty_hint', 'Crea la primera para empezar.') : undefined,
            action: printers.length === 0 ? (
              <button type="button" className="list-btn-primary" onClick={handleCreate}>
                ＋ {tr('printer.new', 'Nueva impresora')}
              </button>
            ) : undefined,
          }}

          actions={{
            extra: (printer) => (
              <>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => handleShowPort(printer)}
                  disabled={!printer.has_ethernet && !(printer.ports?.length > 0)}
                  title={tr('printer.ethernet', 'Ver conexión Ethernet (mapa de puertos)')}
                >
                  <EyeIcon />
                </button>
                <button
                  type="button"
                  className="list-action-btn"
                  onClick={() => handleShowWiFi(printer)}
                  disabled={!printer.has_wifi}
                  title={tr('printer.wifi', 'Ver redes WiFi configuradas')}
                >
                  <WifiIcon />
                </button>
              </>
            ),
            onCopy: handleClone,
            onEdit: handleEdit,
            onDelete: handleDelete,
            deleteTitle: tr('printer.delete_title', 'Eliminar impresora'),
            deleteMessage: (printer) => (
              <>
                {tr('printer.delete_confirm', '¿Seguro que quieres eliminar la impresora')} <strong>{printer.name}</strong>?{' '}
                {tr('printer.delete_hint', 'Se eliminarán también sus puertos y conexiones. No se puede deshacer.')}
              </>
            ),
          }}
        />
      </div>

      {/* Port Modal for Ethernet */}
      {showPortModal && selectedPrinterForPort && (
        <PortMapModal
          element={selectedPrinterForPort}
          companyId={companyId}
          onClose={() => {
            setShowPortModal(false);
            setSelectedPrinterForPort(null);
          }}
          onUpdated={() => { fetchPrinters(); }}
        />
      )}

      {/* WiFi Config Modal */}
      {showWiFiModal && selectedPrinterForPort && (
        <div className="modal show d-block" style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">📡 WiFi - {selectedPrinterForPort.name}</h5>
                <button type="button" className="btn-close" onClick={() => setShowWiFiModal(false)}></button>
              </div>
              <div className="modal-body">
                <ul className="nav nav-tabs" role="tablist">
                  <li className="nav-item" role="presentation">
                    <button className="nav-link active" id="list-tab" data-bs-toggle="tab" data-bs-target="#list-pane" type="button" role="tab">
                      Redes Conectadas
                    </button>
                  </li>
                  <li className="nav-item" role="presentation">
                    <button className="nav-link" id="add-tab" data-bs-toggle="tab" data-bs-target="#add-pane" type="button" role="tab">
                      Agregar Red
                    </button>
                  </li>
                </ul>
                <div className="tab-content">
                  <div className="tab-pane fade show active" id="list-pane" role="tabpanel">
                    <table className="table table-sm mt-3">
                      <thead>
                        <tr>
                          <th>SSID</th>
                          <th>Banda</th>
                          <th>Seguridad</th>
                          <th>Estado</th>
                        </tr>
                      </thead>
                      <tbody>
                        {wifiConfigs.map(cfg => (
                          <tr key={cfg.id}>
                            <td>{cfg.ssid}</td>
                            <td>{cfg.band}</td>
                            <td>{cfg.security}</td>
                            <td>{cfg.is_current ? <span className="badge bg-success">Actual</span> : null}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  <div className="tab-pane fade" id="add-pane" role="tabpanel">
                    <div className="mt-3">
                      <div className="mb-3">
                        <label>SSID</label>
                        <input type="text" className="form-control" id="new_ssid" placeholder="Nombre de la red" />
                      </div>
                      <div className="mb-3">
                        <label>Banda</label>
                        <select className="form-select" id="new_band" defaultValue="2.4GHz">
                          <option value="2.4GHz">2.4GHz</option>
                          <option value="5GHz">5GHz</option>
                          <option value="6GHz">6GHz</option>
                          <option value="2.4/5">2.4/5 GHz</option>
                        </select>
                      </div>
                      <div className="mb-3">
                        <label>Seguridad</label>
                        <select className="form-select" id="new_security" defaultValue="WPA2">
                          <option value="WPA2">WPA2</option>
                          <option value="WPA3">WPA3</option>
                          <option value="None">Abierto</option>
                        </select>
                      </div>
                      <button
                        type="button"
                        className="btn btn-primary"
                        onClick={async () => {
                          const ssid = document.getElementById('new_ssid').value;
                          const band = document.getElementById('new_band').value;
                          const security = document.getElementById('new_security').value;
                          if (ssid) {
                            try {
                              await api.addPrinterWiFiConfig(selectedPrinterForPort.id, { ssid, band, security, is_current: true });
                              await handleShowWiFi(selectedPrinterForPort);
                            } catch (err) {
                              console.error('Error adding WiFi config:', err);
                              alert('Error agregando configuración WiFi');
                            }
                          }
                        }}
                      >
                        Agregar
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}

export default PrintersList;
